import { notification } from "antd"
import { useEffect, useRef } from "react"
import { useLocation, useNavigate } from "react-router"

import { useImportStatus } from "~/hooks/people-os"
import usePeopleOsStore from "~/store/people-os"

const PEOPLE_OS_PATH = "/accounts/people-os"
const NUDGE_KEY = "people-os-import-complete"

// Keeps a "Run in background" CSV import polling after its modal closes
// (ENG-2480 item 7). The workflow id lives in the people-os store and this host
// is mounted at the app root, so the poll survives BOTH closing the modal and
// navigating off the People tab. When the import lands while the modal is shut,
// it nudges the user and re-opens the same success card (with the list prompt) so
// the imported people still get filed instead of disappearing into "All people".
//
// Inert for everyone else: useImportStatus stays disabled until the store holds a
// workflow id, and the query key is shared with the modal's own poll, so at most
// one request is in flight however many mounts observe it.
export function PeopleImportWatcher() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const workflowId = usePeopleOsStore((s) => s.importWorkflowId)
  const modalOpen = usePeopleOsStore((s) => s.importModalOpen)
  const setImportModalOpen = usePeopleOsStore((s) => s.setImportModalOpen)

  const { data: status } = useImportStatus(workflowId)

  // One nudge per import, even though the effect re-runs on every poll tick.
  const nudgedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!workflowId || status?.status !== "SUCCESS") return
    // The user is already looking at the success card — no nudge needed.
    if (modalOpen) return
    if (nudgedFor.current === workflowId) return
    nudgedFor.current = workflowId
    const imported = status.processed_rows ?? 0
    notification.open({
      key: NUDGE_KEY,
      type: "success",
      message: "Import complete",
      description: `${imported} ${
        imported === 1 ? "person was" : "people were"
      } imported. Click here to put them in a list.`,
      duration: 0,
      onClick: () => {
        notification.destroy(NUDGE_KEY)
        if (pathname !== PEOPLE_OS_PATH) navigate(PEOPLE_OS_PATH)
        setImportModalOpen(true)
      }
    })
  }, [workflowId, status, modalOpen, pathname, navigate, setImportModalOpen])

  return null
}

export default PeopleImportWatcher
